import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'

import { FRAME_COLOR_TYPE } from '@/libs/api/types'

import {
  createFrameColorByTypeAtomFamily,
  HEX_COLOR_PATTERN
} from './last-frame-color.atom'

/**
 * 利用者が「この色を既定にする」で決めた、枠の種類ごとの既定の背景色（ユーザー単位、localStorage）
 * - 作成モーダルの初期値は この既定色 → 前回色 → 種類ごとの既定色 の順に使う
 * - 前回色は保存のたびに変わるが、この既定色は利用者が決め直すまで変わらない
 */
export const frameDefaultColorAtomFamily = createFrameColorByTypeAtomFamily(
  'dispatch-frame-default-color'
)

/** 最近使った色として覚える数（ColorPicker のパレット 1 段に収まる数） */
export const RECENT_FRAME_COLORS_LIMIT = 10

/** パレットにある色か（最近使った色には出さない） */
const isPaletteColor = (color: string) =>
  FRAME_COLOR_TYPE.some(({ value }) => value === color)

/**
 * 保存値が壊れている場合（手で書き換えた、旧版の値が残っている）は HEX の値だけ残す。
 * パレットの色を覚えていた旧版の保存値が残っていても、読み出す時にパレットの色を除く
 */
const normalizeRecentColors = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []

  return value
    .filter(
      (color): color is string =>
        typeof color === 'string' &&
        HEX_COLOR_PATTERN.test(color) &&
        !isPaletteColor(color)
    )
    .slice(0, RECENT_FRAME_COLORS_LIMIT)
}

type Params = { userId: number }

/**
 * 予定枠・空き時間枠で最近保存した背景色 Atom（ユーザー単位、localStorage）
 * - 新しい順。同じ色を保存し直すと先頭へ移る（重複は持たない）
 * - パレットにある色は覚えない（Excel の「最近使用した色」と同じく、自由に選んだ色だけ）。
 *   パレットと同じ色を 2 か所に並べると、antd が両方に選択の印を付けてしまうため
 * - 枠の種類をまたいで 1 本の履歴にする（Excel の「最近使用した色」と同じ）
 */
export const recentFrameColorsAtomFamily = atomFamily(
  (params: Params) => {
    const base = atomWithStorage<string[]>(
      `dispatch-frame-recent-colors:${String(params.userId)}`,
      [],
      undefined,
      { getOnInit: true }
    )

    return atom(
      (get) => normalizeRecentColors(get(base)),
      (get, set, frameColor: string) => {
        const color = frameColor.toUpperCase()
        if (isPaletteColor(color)) return
        set(
          base,
          [
            color,
            ...normalizeRecentColors(get(base)).filter((c) => c !== color)
          ].slice(0, RECENT_FRAME_COLORS_LIMIT)
        )
      }
    )
  },
  (a, b) => a.userId === b.userId
)
